import React from 'react';

import { useGetReels } from '../hooks/use-reels';

export type Timeline = {
  id: string;
  url?: string;
  thumbnail?: string;
  duration_secs: number;
  content?: {
    title?: string;
    subtitle?: string;
    body?: string;
  };
  background_color?: string;
  type: 'image' | 'video' | 'text';
};

export type ReelsResource = {
  id: string;
  url: string;
};

export type ReelsResult = {
  url: string;
  versionIdentifier: string;
  createdAt: string;
};

export interface ReelsContext {
  global_settings: {
    quality: 'preview' | 'medium' | 'high';
    font: string;
    background_color: string;
    transition: {
      type:
        | 'none'
        | 'fade'
        | 'fadeblack'
        | 'fadewhite'
        | 'wipeleft'
        | 'wiperight'
        | 'dissolve'
        | 'slideleft'
        | 'slideright';
      duration_secs: number;
      default_image_duration_secs: number;
    };
    watermark?: {
      id: string;
      url: string;
      position: 'top_left' | 'top_right' | 'bottom_left' | 'bottom_right';
      size: 'extra_small' | 'small' | 'medium' | 'large' | 'extra_large';
      opacity: string;
    };
    text_style: {
      title_font_size: number;
      subtitle_font_size: number;
      body_font_size: number;
      color: string;
      box_color: string;
      box_border_width: number;
      box_style: 'text_fit' | 'full_frame';
      alignment: 'left' | 'center' | 'right';
    };
  };
  timeline: Timeline[];
  textSlides: Timeline[];
  resources: ReelsResource[];
  versionIdentifier: string;
  gameCode: string;
  isDirty: boolean;
  preview?: ReelsResult;
  result?: ReelsResult;
  handleChange: (value: Partial<Omit<ReelsContext, 'handleChange'>>) => void;
}

const initialContext: ReelsContext = {
  gameCode: '',
  global_settings: {
    quality: 'preview',
    font: 'Open Sans',
    background_color: '',
    transition: {
      type: 'none',
      duration_secs: 0,
      default_image_duration_secs: 3
    },
    text_style: {
      alignment: 'center',
      color: '#ffffff',
      box_color: '#000000',
      box_border_width: 1,
      box_style: 'text_fit',
      title_font_size: 36,
      subtitle_font_size: 24,
      body_font_size: 18
    }
  },
  timeline: [],
  textSlides: [],
  resources: [],
  versionIdentifier: '',
  isDirty: false,
  handleChange: () => {}
};

const ReelsContext = React.createContext<ReelsContext>(initialContext);

export const ReelsProvider: React.FC<
  React.PropsWithChildren<{ gameCode: string }>
> = ({ gameCode, children }) => {
  const [state, setState] = React.useState<
    Omit<ReelsContext, 'handleChange'>
  >({
    gameCode,
    global_settings: {
      quality: 'preview',
      font: 'Open Sans',
      background_color: '#000000',
      transition: {
        type: 'none',
        duration_secs: 0,
        default_image_duration_secs: 3
      },
      text_style: {
        alignment: 'center',
        color: '#ffffff',
        box_color: '#000000',
        box_border_width: 1,
        box_style: 'text_fit',
        title_font_size: 36,
        subtitle_font_size: 24,
        body_font_size: 18
      }
    },
    timeline: [],
    textSlides: [],
    resources: [],
    versionIdentifier: '',
    isDirty: false
  });

  const { data: reelsDraft, error: reelsError } = useGetReels(gameCode);

  const handleChange = React.useCallback(
    (value: Partial<Omit<ReelsContext, 'handleChange'>>) => {
      const isDirty = value.isDirty !== undefined ? value.isDirty : true;
      setState((prev) => {
        return { ...prev, ...value, isDirty };
      });
    },
    []
  );

  const initialContext: ReelsContext = {
    ...state,
    handleChange
  };

  React.useEffect(() => {
    if (!reelsDraft || reelsError) return;
    if (reelsDraft && reelsDraft.status === 404) return;

    const parsed: ReelsContext = JSON.parse(reelsDraft.projectContent);
    setState((prev) => ({
      ...prev,
      ...(parsed.timeline && {
        timeline: parsed.timeline
      }),
      ...(parsed.global_settings && {
        global_settings: {
          ...prev.global_settings,
          ...parsed.global_settings
        }
      }),
      ...(parsed.resources && {
        resources: parsed.resources
      }),
      ...(parsed.textSlides && {
        textSlides: parsed.textSlides
      }),
      versionIdentifier: reelsDraft.versionIdentifier
    }));
  }, [reelsDraft]);

  return (
    <ReelsContext.Provider value={initialContext}>
      {children}
    </ReelsContext.Provider>
  );
};

export const useReels = () => {
  const reels = React.useContext(ReelsContext);
  return reels;
};
